import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AdminService } from '../../services/adminService';
import { supabase } from '../../services/supabaseClient';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  Users, 
  AlertCircle, 
  DollarSign, 
  ShoppingCart, 
  HelpCircle, 
  UserPlus, 
  CheckCircle, 
  Package, 
  Wrench, 
  MapPin,
  RefreshCw,
  TrendingUp,
  FileText,
  Settings,
  Wallet,
  Banknote,
  X,
  Loader2,
  Calendar,
} from 'lucide-react';

const SERVICE_TYPE_COLORS = {
  Towing: '#1B4F9C',
  'Jump Start': '#f59e0b',
  'Flat Tyre': '#0ea5e9',
  'Fuel Delivery': '#10b981',
  'Key Lockout': '#8b5cf6',
  'Minor Repair': '#64748b',
  Garage: '#ec4899',
  RSA: '#ef4444',
  Unknown: '#94a3b8',
  Other: '#a8a29e',
};

const PIE_FALLBACK_COLORS = ['#6366f1', '#14b8a6', '#f97316', '#3b82f6', '#e11d48', '#84cc16'];

function sliceColor(name, index) {
  return SERVICE_TYPE_COLORS[name] || PIE_FALLBACK_COLORS[index % PIE_FALLBACK_COLORS.length];
}

function ymdLocal(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function daysAgoYmd(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return ymdLocal(d);
}

function monthStartYmd() {
  const d = new Date();
  return ymdLocal(new Date(d.getFullYear(), d.getMonth(), 1));
}

const StatCard = ({ title, value, icon: Icon, color, trend, onPress, isHidden, onToggleVisibility }) => {
  const displayValue = isHidden ? '••••' : value;
  
  return (
    <div 
      className={`bg-white rounded-xl p-5 shadow-sm border border-gray-200 hover:shadow-md transition h-full cursor-pointer`}
      onClick={onPress}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center`} style={{ backgroundColor: `${color}20` }}>
          <Icon size={24} color={color} />
        </div>
        <div className="flex items-center space-x-2">
          {trend && (
            <span className="px-2 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded">
              {trend}
            </span>
          )}
          {onToggleVisibility && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleVisibility();
              }}
              className="p-1 hover:bg-gray-100 rounded"
            >
              {isHidden ? (
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-1">{displayValue}</h3>
      <p className="text-sm text-gray-500">{title}</p>
    </div>
  );
};

const StatusPill = ({ label, status }) => (
  <div className={`inline-flex items-center space-x-2 px-3 py-1.5 rounded-full ${
    status === 'online' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
  }`}>
    <div className={`w-2 h-2 rounded-full ${status === 'online' ? 'bg-green-500' : 'bg-red-500'}`} />
    <span className="text-sm font-medium">{label}</span>
  </div>
);

const ProgressBar = ({ label, current, total, color }) => {
  const percentage = total > 0 ? Math.min(100, (current / total) * 100) : 0;
  
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className="text-sm font-semibold" style={{ color }}>{Math.round(percentage)}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="h-2 rounded-full transition-all"
          style={{ width: `${percentage}%`, backgroundColor: color }}
        />
      </div>
      <p className="text-xs text-gray-500 mt-1">{current}/{total}</p>
    </div>
  );
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [hiddenCards, setHiddenCards] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [moneyModal, setMoneyModal] = useState(null);
  const [moneyRows, setMoneyRows] = useState([]);
  const [moneyLoading, setMoneyLoading] = useState(false);
  const [pieFrom, setPieFrom] = useState(() => ymdLocal());
  const [pieTo, setPieTo] = useState(() => ymdLocal());
  const [statusModal, setStatusModal] = useState(null);
  const [statusRows, setStatusRows] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);

  const { data: stats, isFetching: statsFetching, refetch } = useQuery({
    queryKey: ['adminStats'],
    queryFn: AdminService.getAdminStats,
    refetchInterval: 30000,
    placeholderData: AdminService.emptyAdminStats(),
  });

  const { data: money, refetch: refetchMoney } = useQuery({
    queryKey: ['adminMoneyStats'],
    queryFn: AdminService.getPartnerMoneyStats,
    refetchInterval: 30000,
    placeholderData: { partnerAdvance: 0, partnerRepayCollected: 0, ticketRazorpayRevenue: 0 },
  });

  const { data: systemStatus, refetch: refetchStatus } = useQuery({
    queryKey: ['systemStatus'],
    queryFn: AdminService.getSystemStatus,
    refetchInterval: 60000, // Refresh every minute
  });

  const { data: serviceTypeBreakdown, isFetching: pieFetching, refetch: refetchServiceTypes } = useQuery({
    queryKey: ['adminServiceTypeBreakdown', pieFrom, pieTo],
    queryFn: () => AdminService.getServiceTypeBreakdown({ from: pieFrom, to: pieTo }),
    refetchInterval: 60000,
  });

  const applyPiePreset = (preset) => {
    if (preset === 'all') {
      setPieFrom('');
      setPieTo('');
      return;
    }
    const today = ymdLocal();
    if (preset === 'today') {
      setPieFrom(today);
      setPieTo(today);
      return;
    }
    if (preset === '7d') {
      setPieFrom(daysAgoYmd(6));
      setPieTo(today);
      return;
    }
    if (preset === '30d') {
      setPieFrom(daysAgoYmd(29));
      setPieTo(today);
      return;
    }
    if (preset === 'month') {
      setPieFrom(monthStartYmd());
      setPieTo(today);
    }
  };

  const piePreset = (() => {
    const today = ymdLocal();
    if (!pieFrom && !pieTo) return 'all';
    if (pieFrom === today && pieTo === today) return 'today';
    if (pieFrom === daysAgoYmd(6) && pieTo === today) return '7d';
    if (pieFrom === daysAgoYmd(29) && pieTo === today) return '30d';
    if (pieFrom === monthStartYmd() && pieTo === today) return 'month';
    return '';
  })();

  const toggleCard = (key) => {
    setHiddenCards(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const openStatusList = async (status) => {
    setStatusModal(status);
    setStatusLoading(true);
    setStatusRows([]);
    try {
      const rows = await AdminService.getServiceTypeStatusJobs({
        from: pieFrom,
        to: pieTo,
        status,
      });
      setStatusRows(rows);
    } catch (error) {
      console.warn('status list failed', error?.message || error);
      setStatusRows([]);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([refetch(), refetchMoney(), refetchStatus(), refetchServiceTypes()]);
    setRefreshing(false);
  };

  const formatCurrency = (value) => {
    return `₹${Math.round(value || 0).toLocaleString('en-IN')}`;
  };

  const formatMoneyExact = (value) =>
    `₹${Number(value || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const partnerNames = async (ids) => {
    const unique = [...new Set((ids || []).filter(Boolean).map(String))];
    const map = {};
    if (!unique.length) return map;
    const { data } = await supabase
      .from('mechanics')
      .select('id, mechanic_name, shop_name, code')
      .in('id', unique)
      .limit(unique.length);
    (data || []).forEach((row) => {
      map[row.id] = row.shop_name || row.mechanic_name || row.code || 'Partner';
    });
    return map;
  };

  const openAdvanceModal = async () => {
    setMoneyModal('advance');
    setMoneyLoading(true);
    setMoneyRows([]);
    try {
      const { data } = await supabase
        .from('partner_credit_grants')
        .select('id, mechanic_id, amount, outstanding, note, created_at')
        .eq('repayable', true)
        .order('created_at', { ascending: false })
        .limit(200);
      const rows = data || [];
      const names = await partnerNames(rows.map((r) => r.mechanic_id));
      setMoneyRows(
        rows.map((row) => ({
          id: row.id,
          name: names[row.mechanic_id] || 'Partner',
          amount: Number(row.amount || 0),
          extra: Number(row.outstanding || 0),
          extraLabel: 'due',
          note: row.note,
          at: row.created_at,
        })),
      );
    } catch (e) {
      console.error(e);
      setMoneyRows([]);
    } finally {
      setMoneyLoading(false);
    }
  };

  const openRepayModal = async () => {
    setMoneyModal('repay');
    setMoneyLoading(true);
    setMoneyRows([]);
    try {
      const { data } = await supabase
        .from('partner_credit_repayments')
        .select(
          'id, mechanic_id, amount, method, gst_amount, total_amount, razorpay_payment_id, invoice_number, created_at, note',
        )
        .eq('status', 'paid')
        .in('method', ['razorpay', 'cash'])
        .order('created_at', { ascending: false })
        .limit(200);
      const rows = data || [];
      const names = await partnerNames(rows.map((r) => r.mechanic_id));
      setMoneyRows(
        rows.map((row) => ({
          id: row.id,
          name: names[row.mechanic_id] || 'Partner',
          amount: AdminService.repayRazorpayAmount(row),
          extra: row.razorpay_payment_id || row.method,
          extraLabel: '',
          note: [row.invoice_number, row.razorpay_payment_id ? '' : row.method, row.note]
            .filter(Boolean)
            .join(' · '),
          at: row.created_at,
        })),
      );
    } catch (e) {
      console.error(e);
      setMoneyRows([]);
    } finally {
      setMoneyLoading(false);
    }
  };

  const openTicketRazorpayModal = async () => {
    setMoneyModal('tickets');
    setMoneyLoading(true);
    setMoneyRows([]);
    try {
      const rows = await AdminService.listPartnerTicketRazorpayPurchases(200);
      const names = await partnerNames(rows.map((r) => r.mechanic_id));
      setMoneyRows(
        rows.map((row) => ({
          id: row.id,
          name: names[row.mechanic_id] || 'Partner',
          amount: AdminService.ticketRazorpayAmount(row),
          extra: row.razorpay_payment_id,
          extraLabel: '',
          note: row.invoice_number || '',
          at: row.paid_at || row.created_at,
        })),
      );
    } catch (e) {
      console.error(e);
      setMoneyRows([]);
    } finally {
      setMoneyLoading(false);
    }
  };

  const computeDelta = (value) => {
    if (!value || value <= 0) return '+0%';
    const base = Math.max(1, Math.floor(value / 10));
    const delta = Math.min(25, (value % (base + 5)) + 1);
    return `+${delta}%`;
  };

  const viewStats = {
    ...(stats || AdminService.emptyAdminStats()),
    partnerAdvance: money?.partnerAdvance ?? 0,
    partnerRepayCollected: money?.partnerRepayCollected ?? 0,
    ticketRazorpayRevenue: money?.ticketRazorpayRevenue ?? 0,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-1">System overview and management</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing || statsFetching}
          className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 shrink-0 self-start"
        >
          <RefreshCw size={20} className={refreshing || statsFetching ? 'animate-spin' : ''} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Grid */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Key Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <StatCard
            title="Total Users"
            value={viewStats.totalUsers || 0}
            icon={Users}
            color="#2563eb"
            trend={computeDelta(viewStats.totalUsers || 0)}
            isHidden={hiddenCards['Total Users']}
            onToggleVisibility={() => toggleCard('Total Users')}
          />
          <StatCard
            title="Ongoing Complaints"
            value={viewStats.activeRequests || 0}
            icon={AlertCircle}
            color="#f59e0b"
            trend={computeDelta(viewStats.activeRequests || 0)}
            onPress={() => navigate('/mechanic-management?feed=open')}
            isHidden={hiddenCards['Ongoing Complaints']}
            onToggleVisibility={() => toggleCard('Ongoing Complaints')}
          />
          <StatCard
            title="Partner Advance Given"
            value={formatCurrency(viewStats.partnerAdvance || 0)}
            icon={Wallet}
            color="#d97706"
            onPress={openAdvanceModal}
            isHidden={hiddenCards['Partner Advance Given']}
            onToggleVisibility={() => toggleCard('Partner Advance Given')}
          />
          <StatCard
            title="Repay Collected"
            value={formatCurrency(viewStats.partnerRepayCollected || 0)}
            icon={Banknote}
            color="#0f766e"
            onPress={openRepayModal}
            isHidden={hiddenCards['Repay Collected']}
            onToggleVisibility={() => toggleCard('Repay Collected')}
          />
          <StatCard
            title="Ticket Razorpay"
            value={formatCurrency(viewStats.ticketRazorpayRevenue || 0)}
            icon={DollarSign}
            color="#10b981"
            onPress={openTicketRazorpayModal}
            isHidden={hiddenCards['Ticket Razorpay']}
            onToggleVisibility={() => toggleCard('Ticket Razorpay')}
          />
          <StatCard
            title="TL Confirmation"
            value={viewStats.pendingOrders || 0}
            icon={ShoppingCart}
            color="#ef4444"
            trend={computeDelta(viewStats.pendingOrders || 0)}
            isHidden={hiddenCards['TL Confirmation']}
            onToggleVisibility={() => toggleCard('TL Confirmation')}
          />
          <StatCard
            title="Support Tickets"
            value={viewStats.supportTickets || 0}
            icon={HelpCircle}
            color="#8b5cf6"
            trend={computeDelta(viewStats.supportTickets || 0)}
            isHidden={hiddenCards['Support Tickets']}
            onToggleVisibility={() => toggleCard('Support Tickets')}
          />
          <StatCard
            title="Converted Leads"
            value={viewStats.totalLeads || 0}
            icon={UserPlus}
            color="#06b6d4"
            trend={computeDelta(viewStats.totalLeads || 0)}
            isHidden={hiddenCards['Converted Leads']}
            onToggleVisibility={() => toggleCard('Converted Leads')}
          />
          <StatCard
            title="Delivered Leads"
            value={viewStats.deliveredLeads || 0}
            icon={CheckCircle}
            color="#059669"
            trend={computeDelta(viewStats.deliveredLeads || 0)}
            isHidden={hiddenCards['Delivered Leads']}
            onToggleVisibility={() => toggleCard('Delivered Leads')}
          />
          <StatCard
            title="Completed Complaints"
            value={viewStats.totalMasterOrders || 0}
            icon={Package}
            color="#a855f7"
            trend={computeDelta(viewStats.totalMasterOrders || 0)}
            isHidden={hiddenCards['Completed Complaints']}
            onToggleVisibility={() => toggleCard('Completed Complaints')}
          />
          <StatCard
            title="Available Mechanics"
            value={viewStats.pendingAssignments || 0}
            icon={Wrench}
            color="#eab308"
            trend={computeDelta(viewStats.pendingAssignments || 0)}
            isHidden={hiddenCards['Available Mechanics']}
            onToggleVisibility={() => toggleCard('Available Mechanics')}
          />
          <StatCard
            title="Serviceable Pincodes"
            value={viewStats.uniqueMechanicPincodes || 0}
            icon={MapPin}
            color="#0ea5e9"
            trend={computeDelta(viewStats.uniqueMechanicPincodes || 0)}
            isHidden={hiddenCards['Serviceable Pincodes']}
            onToggleVisibility={() => toggleCard('Serviceable Pincodes')}
          />
        </div>
      </div>

      {/* Service type mix */}
      <div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Service type</h2>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-wrap gap-1">
              {[
                { id: 'today', label: 'Today' },
                { id: '7d', label: '7 days' },
                { id: '30d', label: '30 days' },
                { id: 'month', label: 'This month' },
                { id: 'all', label: 'All' },
              ].map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => applyPiePreset(p.id)}
                  className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${
                    piePreset === p.id
                      ? 'bg-slate-900 text-white'
                      : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <label className="text-xs text-gray-500">
              From
              <span className="mt-1 flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 py-1.5">
                <Calendar size={14} className="text-gray-400" />
                <input
                  type="date"
                  value={pieFrom}
                  max={pieTo || ymdLocal()}
                  onChange={(e) => setPieFrom(e.target.value)}
                  className="text-sm text-gray-800 outline-none"
                />
              </span>
            </label>
            <label className="text-xs text-gray-500">
              To
              <span className="mt-1 flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 py-1.5">
                <Calendar size={14} className="text-gray-400" />
                <input
                  type="date"
                  value={pieTo}
                  min={pieFrom || undefined}
                  max={ymdLocal()}
                  onChange={(e) => setPieTo(e.target.value)}
                  className="text-sm text-gray-800 outline-none"
                />
              </span>
            </label>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          {pieFetching && !serviceTypeBreakdown?.slices?.length ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
              <Loader2 size={16} className="animate-spin" />
              Loading…
            </div>
          ) : !serviceTypeBreakdown?.slices?.length ? (
            <p className="py-10 text-center text-sm text-gray-500">No complaints in this date range</p>
          ) : (
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 items-center ${pieFetching ? 'opacity-60' : ''}`}>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={serviceTypeBreakdown.slices}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={62}
                      outerRadius={110}
                      paddingAngle={2}
                      stroke="#fff"
                      strokeWidth={2}
                    >
                      {serviceTypeBreakdown.slices.map((slice, index) => (
                        <Cell key={slice.name} fill={sliceColor(slice.name, index)} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        borderRadius: '10px',
                        border: 'none',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        fontSize: '13px',
                      }}
                      formatter={(value, name) => {
                        const total = serviceTypeBreakdown.total || 1;
                        const pct = Math.round((Number(value || 0) / total) * 100);
                        return [`${Number(value).toLocaleString('en-IN')} (${pct}%)`, name];
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">
                  {serviceTypeBreakdown.total.toLocaleString('en-IN')} complaints
                  {pieFrom || pieTo
                    ? ` · ${pieFrom || '…'} → ${pieTo || '…'}`
                    : ' · all time'}
                </p>
                <ul className="space-y-2">
                  {serviceTypeBreakdown.slices.map((slice, index) => {
                    const pct = serviceTypeBreakdown.total
                      ? Math.round((slice.value / serviceTypeBreakdown.total) * 100)
                      : 0;
                    return (
                      <li key={slice.name} className="flex items-center justify-between gap-3 text-sm">
                        <span className="flex items-center gap-2 min-w-0">
                          <span
                            className="w-2.5 h-2.5 rounded-full shrink-0"
                            style={{ background: sliceColor(slice.name, index) }}
                          />
                          <span className="font-medium text-gray-800 truncate">{slice.name}</span>
                        </span>
                        <span className="tabular-nums text-gray-500 shrink-0">
                          {slice.value.toLocaleString('en-IN')} · {pct}%
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
          {serviceTypeBreakdown ? (
            <div className={`mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 border-t border-gray-100 pt-5 ${pieFetching ? 'opacity-60' : ''}`}>
              {[
                {
                  key: 'complete',
                  label: 'Complete',
                  value: serviceTypeBreakdown.statuses?.complete || 0,
                  hint: 'completed this range · view list',
                  icon: CheckCircle,
                  wrap: 'bg-emerald-50 border-emerald-100 hover:border-emerald-300',
                  iconWrap: 'bg-emerald-100 text-emerald-700',
                  valueCls: 'text-emerald-800',
                },
                {
                  key: 'cancel',
                  label: 'Cancel',
                  value: serviceTypeBreakdown.statuses?.cancel || 0,
                  hint: 'cancelled this range · view list',
                  icon: X,
                  wrap: 'bg-rose-50 border-rose-100 hover:border-rose-300',
                  iconWrap: 'bg-rose-100 text-rose-700',
                  valueCls: 'text-rose-800',
                },
                {
                  key: 'open',
                  label: 'Open',
                  value: serviceTypeBreakdown.statuses?.open || 0,
                  hint: 'created this range · view list',
                  icon: AlertCircle,
                  wrap: 'bg-amber-50 border-amber-100 hover:border-amber-300',
                  iconWrap: 'bg-amber-100 text-amber-800',
                  valueCls: 'text-amber-900',
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    type="button"
                    key={item.key}
                    onClick={() => openStatusList(item.key)}
                    className={`rounded-xl border px-4 py-3 text-left transition hover:shadow-sm ${item.wrap}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${item.iconWrap}`}>
                        <Icon size={14} />
                      </span>
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                        {item.label}
                      </span>
                    </div>
                    <p className={`text-2xl font-bold tabular-nums ${item.valueCls}`}>
                      {item.value.toLocaleString('en-IN')}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{item.hint}</p>
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>

      {/* System Status */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">System Status</h2>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex flex-wrap gap-3">
            <StatusPill label="Database" status={systemStatus?.database || 'offline'} />
            <StatusPill label="Payments" status={systemStatus?.payments || 'offline'} />
            <StatusPill label="Notifications" status={systemStatus?.notifications || 'offline'} />
            <StatusPill label="Chat" status={systemStatus?.chat || 'offline'} />
          </div>
        </div>
      </div>

      {/* Progress Overview */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Operational Progress</h2>
        <div className="space-y-4">
          <ProgressBar
            label="Services Completed"
            current={viewStats.completedServices || 0}
            total={(viewStats.completedServices || 0) + (viewStats.activeRequests || 0)}
            color="#22c55e"
          />
          <ProgressBar
            label="Assignments Pending"
            current={viewStats.pendingAssignments || 0}
            total={(viewStats.pendingAssignments || 0) + (viewStats.activeRequests || 0)}
            color="#f59e0b"
          />
          <ProgressBar
            label="Orders Pending Payment"
            current={viewStats.pendingOrders || 0}
            total={(viewStats.pendingOrders || 0) + Math.max(1, (viewStats.totalMasterOrders || 0))}
            color="#ef4444"
          />
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <a
            href="/users"
            className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition flex items-center space-x-3"
          >
            <Users size={24} className="text-primary-500" />
            <span className="font-medium text-gray-900">Manage Users</span>
          </a>
          <a
            href="/sales-report"
            className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition flex items-center space-x-3"
          >
            <FileText size={24} className="text-primary-500" />
            <span className="font-medium text-gray-900">Sales Reports</span>
          </a>
          <a
            href="/shipment-tracking"
            className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition flex items-center space-x-3"
          >
            <Package size={24} className="text-primary-500" />
            <span className="font-medium text-gray-900">Shipment Tracking</span>
          </a>
          <a
            href="/settings"
            className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition flex items-center space-x-3"
          >
            <Settings size={24} className="text-primary-500" />
            <span className="font-medium text-gray-900">Settings</span>
          </a>
          <a
            href="/more"
            className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition flex items-center space-x-3"
          >
            <TrendingUp size={24} className="text-primary-500" />
            <span className="font-medium text-gray-900">More Options</span>
          </a>
        </div>
      </div>

      {statusModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40"
            aria-label="Close"
            onClick={() => setStatusModal(null)}
          />
          <div className="relative z-10 flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {statusModal === 'complete'
                    ? 'Complete complaints'
                    : statusModal === 'cancel'
                      ? 'Cancelled complaints'
                      : 'Open complaints'}
                </h3>
                <p className="mt-0.5 text-xs text-slate-500">
                  {pieFrom || pieTo
                    ? `${pieFrom || '…'} → ${pieTo || '…'}`
                    : 'All time'}
                  {statusRows.length ? ` · ${statusRows.length} shown` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setStatusModal(null)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"
              >
                <X size={18} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
              {statusLoading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
                  <Loader2 size={16} className="animate-spin" />
                  Loading…
                </div>
              ) : statusRows.length === 0 ? (
                <p className="py-10 text-center text-sm text-slate-500">No complaints in this range</p>
              ) : (
                statusRows.map((row) => (
                  <button
                    type="button"
                    key={row.id}
                    onClick={() => {
                      setStatusModal(null);
                      navigate(`/service_advisor/complaints/${row.id}`);
                    }}
                    className="flex w-full items-start justify-between gap-3 border-b border-slate-100 py-2.5 text-left hover:bg-slate-50"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900">
                        {row.customer_name || 'Customer'}
                      </p>
                      <p className="truncate text-[11px] text-slate-500">
                        {row.service_type || 'Service'}
                        {row.contact_number ? ` · ${row.contact_number}` : ''}
                      </p>
                      <p className="truncate text-[11px] text-slate-400">
                        {row.pickup_address || row.assigned_mechanic_name || '—'}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-[11px] font-semibold capitalize text-slate-600">
                        {row.request_status || row.complaint_status || statusModal}
                      </p>
                      <p className="text-[11px] text-slate-400">
                        {formatDate(
                          statusModal === 'complete'
                            ? row.mechanic_completed_datetime
                            : statusModal === 'cancel'
                              ? row.cancelled_at
                              : row.complaint_registered_at || row.requested_at,
                        )}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      ) : null}

      {moneyModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40"
            aria-label="Close"
            onClick={() => setMoneyModal(null)}
          />
          <div className="relative z-10 flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {moneyModal === 'advance'
                    ? 'Partner advance given'
                    : moneyModal === 'tickets'
                      ? 'Ticket Razorpay collected'
                      : 'Repay collected'}
                </h3>
                <p className="mt-0.5 text-xs text-slate-500">
                  {moneyModal === 'advance'
                    ? 'Repayable advances given from admin'
                    : moneyModal === 'tickets'
                      ? 'Paid partner ticket packs collected on Razorpay (not wallet repay)'
                      : 'Razorpay / cash repayments received (GST-inclusive, with payment ID)'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setMoneyModal(null)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"
              >
                <X size={18} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
              {moneyLoading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
                  <Loader2 size={16} className="animate-spin" />
                  Loading…
                </div>
              ) : moneyRows.length === 0 ? (
                <p className="py-10 text-center text-sm text-slate-500">No records yet</p>
              ) : (
                moneyRows.map((row) => (
                  <div
                    key={row.id}
                    className="flex items-start justify-between gap-3 border-b border-slate-100 py-2.5"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900">{row.name}</p>
                      <p className="text-[11px] text-slate-500">
                        {formatDate(row.at)}
                        {row.note ? ` · ${row.note}` : ''}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-sm font-bold tabular-nums text-slate-900">
                        {formatMoneyExact(row.amount)}
                      </p>
                      {row.extraLabel ? (
                        <p className="text-[11px] font-semibold text-amber-700">
                          ₹{Number(row.extra || 0).toLocaleString('en-IN')} {row.extraLabel}
                        </p>
                      ) : row.extra ? (
                        <p className="max-w-[160px] break-all text-[10px] font-semibold text-slate-500">
                          {row.extra}
                        </p>
                      ) : null}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Dashboard;

